import React from 'react';
import { CreditCardIcon } from 'lucide-react';
import { product } from '../data/product';
import { trackPixelEvent } from '../utils/metaPixel';
export const CHECKOUT_URL = product.checkoutUrl;
export type CtaTracking = {
  name: string;
  id: string;
  price: number;
  currency: string;
};
type CtaButtonProps = {
  label?: string;
  size?: 'md' | 'lg';
  className?: string;
  /** Checkout destination. Ignored when scrollToId is set. */
  href?: string;
  /** When set, the button scrolls to that element on the page instead of going to checkout. */
  scrollToId?: string;
  tone?: 'gold' | 'ember';
  tracking?: CtaTracking;
};
export function CtaButton({
  label = '¡QUIERO MI CRÉDITO LIMPIO AHORA!',
  size = 'lg',
  className = '',
  href = CHECKOUT_URL,
  scrollToId,
  tone = 'gold',
  tracking = {
    name: product.name,
    id: product.id,
    price: product.price,
    currency: product.currency
  }
}: CtaButtonProps) {
  const sizing = size === 'lg' ? 'w-full sm:w-auto px-5 py-3.5 text-[0.9rem] sm:px-8 sm:py-5 sm:text-base' : 'px-4 py-2.5 text-xs';
  const toning = tone === 'ember' ? 'bg-ember text-coal-deep hover:bg-ember-light focus-visible:ring-ember/40' : 'bg-gold text-navy-deep hover:bg-gold-light focus-visible:ring-gold/40';
  const classes = `group inline-flex items-center justify-center gap-2 rounded-lg text-center font-bold uppercase leading-tight tracking-wide shadow-lift outline-none transition-[transform,background-color,box-shadow] duration-150 ease-smooth focus-visible:ring-4 active:translate-y-px ${toning} ${sizing} ${className}`;
  const icon = <CreditCardIcon className={size === 'lg' ? 'h-4 w-4 shrink-0 sm:h-5 sm:w-5' : 'h-4 w-4 shrink-0'} aria-hidden="true" data-id="element-22" data-mp="1jzbhax75hnd" />;
  if (scrollToId) {
    const handleScroll = (event: React.MouseEvent<HTMLAnchorElement>) => {
      const target = document.getElementById(scrollToId);
      if (!target) return;
      event.preventDefault();
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    };
    return <a href={`#${scrollToId}`} onClick={handleScroll} className={classes} data-id="element-23" data-mp="i4ezuk9rav4">
        {icon}
        <span data-id="element-24" data-mp="3rwa8k68mugd1">{label}</span>
      </a>;
  }
  const handleCheckout = () => {
    trackPixelEvent('AddToCart', {
      content_name: tracking.name,
      content_ids: tracking.id,
      content_type: 'product',
      value: tracking.price,
      currency: tracking.currency
    });
  };

  // Sin URL de checkout configurada: el botón registra el evento sin navegar a ningún sitio.
  if (!href) {
    return <button type="button" onClick={handleCheckout} className={classes} data-id="element-25" data-mp="eip0iyhkjksb">
        {icon}
        <span data-id="element-26" data-mp="2l7brct7pvm7g">{label}</span>
      </button>;
  }
  return <a href={href} target="_blank" rel="noopener noreferrer" onClick={handleCheckout} className={classes} data-id="element-27" data-mp="2l9y73g4rh80r">
      {icon}
      <span data-id="element-28" data-mp="vsaqy9yokuir">{label}</span>
    </a>;
}